import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Star,
  Edit,
  Trash2,
  Copy,
  MoreVertical,
  ChevronRight,
  RotateCw,
  X,
  Filter,
  Download,
  Wand2,
  Sparkles,
  Loader2,
  HelpCircle,
  Settings2,
  Info,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "sonner";
import { useUser } from "../../../../context/UserContext";
import { useCreatePermissions } from "../../../../hooks/useCreatePermissions";
import LockButton from "../../../Lock/LockButton";
import LockBadge from "../../../Lock/LockBadge";
import { useHat } from "../../../../context/HatContext";
import DepartmentFilterButtons from "./DepartmentFilterButtons";

// Define Hat/VA type
interface Hat {
  id?: string;
  hat_name: string;
  // Admin lock. Only ever set on rows the viewer locked themselves -- the API
  // hides everyone else's locked VAs from this list entirely.
  locked_by?: string | null;
  locked_at?: string | null;
  department?: string;
  summary: string;
  initial_prompt?: string;
  directions: string;
  is_public: boolean;
  feature_keys: {
    image_creation: { enabled: boolean };
    internet_browsing: { enabled: boolean };
    kyva_llm: { enabled: boolean };
    kyva_connectors: { enabled: boolean };
    include_knowledge_base: {
      enabled: boolean;
      knowledge_base_ids: string[];
    };
  };
  favorites?: {[key: string]: boolean | { BOOL: boolean }};
  selected_icon?: File | null;
  created_at?: string;
  updated_at?: string;
  userId?: string;
  user_id?: string;
  isShared?: boolean;
}

// Connector as returned by /connectors/info -- only the fields the filter needs.
interface ConnectorOption {
  id: string;
  name: string;
  type: string;
  status?: string;
}

interface MobileVAListViewProps {
  onCreateClick: () => void;
  onEditClick: (va: Hat) => void;
  onDuplicateClick?: (va: Hat) => void;
  onDeleteClick?: (va: Hat, e?: React.MouseEvent) => void;
  onFavoriteToggle?: (va: Hat, e?: React.MouseEvent) => void;
  onClose?: () => void; 
  vaList: Hat[];
  loading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isAdmin?: boolean;
  userId: string;
  connectors?: ConnectorOption[];
  connectorFilter?: string | null;
  onConnectorFilterChange?: (connectorId: string | null) => void;
}

const MobileVAListView: React.FC<MobileVAListViewProps> = ({
  onCreateClick,
  onEditClick,
  onDuplicateClick,
  onDeleteClick,
  onFavoriteToggle,
  onClose,
  vaList,
  loading,
  searchQuery,
  onSearchChange,
  isAdmin = false,
  userId,
  connectors = [],
  connectorFilter = null,
  onConnectorFilterChange
}) => {
  const { canCreateVA } = useCreatePermissions();
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  // Card whose created/updated details panel is expanded.
  const [infoOpenId, setInfoOpenId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempFilter, setTempFilter] = useState<string>("all");
  const [tempConnectorFilter, setTempConnectorFilter] = useState<string | null>(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vaToDelete, setVaToDelete] = useState<Hat | null>(null);
  const [showAIModifyModal, setShowAIModifyModal] = useState(false);
  const [vaToModify, setVaToModify] = useState<Hat | null>(null);
  const [aiInstructions, setAiInstructions] = useState("");
  const [isAIModifying, setIsAIModifying] = useState(false);
  const [showAIHelp, setShowAIHelp] = useState(false);

  const {
    deleteHatById: deleteVA,
    fetchHatsListWithFilter,
    fetchHatsList,
    currentFilter,
    updateCurrentFilter,
    currentDepartmentFilter,
    updateCurrentDepartmentFilter,
    setSelectedHat,
    modifyHatWithLLM,
  } = useHat();

  // Create a stable reference to prevent re-renders
  const fetchData = React.useCallback(() => {
    if (!userId) return;

    const loadVAs = async () => {
      try {
        setIsFilterLoading(true);
        if (isAdmin && currentFilter === 'all') {
          await fetchHatsList();
        } else {
          await fetchHatsListWithFilter(currentFilter);
        }
      } catch (error) {
        console.error("Error loading VAs:", error);
        toast.error("Failed to load Virtual Assistants");
      } finally {
        setIsFilterLoading(false);
      }
    };

    loadVAs();
  }, [userId, currentFilter, isAdmin]); // Remove function dependencies

  // Use the stable reference in useEffect
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Enhanced filtering that includes department filter
  const filteredVAs = React.useMemo(() => {
    let filtered = vaList;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((va: Hat) => {
        const hatName = va.hat_name?.toLowerCase() || '';
        const summary = va.summary?.toLowerCase() || '';
        return hatName.includes(query) || summary.includes(query);
      });
    }

    // Apply department filter
    if (currentDepartmentFilter) {
      filtered = filtered.filter((va: Hat) => {
        const vaDepartment = va.department;
        
        // Handle various department field states
        if (!vaDepartment || vaDepartment.trim() === '') {
          return false; // VA has no department, doesn't match selected department
        }
        
        return vaDepartment.trim() === currentDepartmentFilter.trim();
      });
    }

    return filtered;
  }, [vaList, searchQuery, currentDepartmentFilter]);

  // Helper function to check if a hat is favorited (handles both DynamoDB formats)
  const isVAFavorite = (va: Hat) => {
    if (!va.favorites || !userId) return false;
    
    const favoriteValue = va.favorites[userId];
    
    // Handle DynamoDB BOOL structure: { "user_id": { "BOOL": true } }
    if (typeof favoriteValue === 'object' && favoriteValue?.BOOL !== undefined) {
      return favoriteValue.BOOL === true;
    }
    
    // Handle simple boolean: { "user_id": true }
    return favoriteValue === true;
  };
  
  const canEditVA = (va: Hat): boolean => {
    if (isAdmin) return true;
    const currentUserId = userId?.toLowerCase() || "";
    const isOwner = (va as any).user_id === currentUserId || (va as any).userId === currentUserId;
    if (isOwner) return true;
    const isManagerUser = (va as any).managers && Array.isArray((va as any).managers)
      ? (va as any).managers.includes(currentUserId)
      : false;
    return isManagerUser;
  };

  const getVAType = (va: Hat, currentUserId: string, currentFilterType?: string) => {
    const isOwner = (va as any).user_id === currentUserId || (va as any).userId === currentUserId;
    const isManager = (va as any).managers && Array.isArray((va as any).managers)
      ? (va as any).managers.includes(currentUserId)
      : false;

    if (isOwner) {
      return "My VA";
    } else if (isManager) {
      return "Managed VA";
    } else if ((va as any).is_public) {
      return "Public VA";
    } else {
      return "Shared VA";
    }
  };

  // Handle VA selection for chat
const handleVASelection = (va: Hat) => {
  if (va.id) {
    setSelectedHat(va as any);
    toast.success(`Selected Virtual Assistant: ${va.hat_name}`);
    // Close the modal after successful selection
    if (onClose) {
      onClose();
    }
  } else {
    toast.error("Cannot select: Missing VA ID");
  }
};

  // Handle filter modal open
  const handleFilterClick = () => {
    setTempFilter(currentFilter);
    setTempConnectorFilter(connectorFilter);
    setShowFilterModal(true);
  };

  // Handle filter apply
  const handleApplyFilter = async () => {
    onConnectorFilterChange?.(tempConnectorFilter);

    if (tempFilter !== currentFilter) {
      setShowFilterModal(false);
      
      try {
        updateCurrentFilter(tempFilter);
        onSearchChange(""); // Clear search when changing filters
        // The context's useEffect will handle the data loading
      } catch (error) {
        console.error("Error applying filter:", error);
        toast.error("Failed to apply filter");
      }
    } else {
      setShowFilterModal(false);
    }
  };

  // Handle clear filters
  const handleClearFilters = async () => {
    const defaultFilter = "all";
    setTempConnectorFilter(null);
    onConnectorFilterChange?.(null);

    if (currentFilter !== defaultFilter) {
      setIsFilterLoading(true);
      setShowFilterModal(false);
      
      try {
        setTempFilter(defaultFilter);
        updateCurrentFilter(defaultFilter);
        onSearchChange("");
        updateCurrentDepartmentFilter(null); // Clear department filter when clearing all filters
        // The useEffect will trigger the appropriate fetch function
      } catch (error) {
        console.error("Error clearing filters:", error);
        toast.error("Failed to clear filters");
      } finally {
        setIsFilterLoading(false);
      }
    } else {
      setTempFilter(defaultFilter);
      setShowFilterModal(false);
    }
  };

  // Handle department filter change
  const handleDepartmentChange = (departmentName: string | null) => {
    updateCurrentDepartmentFilter(departmentName);
  };

  // Clear all filters function
  const handleClearAllFilters = () => {
    onSearchChange("");
    updateCurrentDepartmentFilter(null);
    setTempConnectorFilter(null);
    onConnectorFilterChange?.(null);
    if (currentFilter !== "all") {
      updateCurrentFilter("all");
    }
  };

  // Export VA function
  const handleExportVA = (va: Hat) => {
    try {
      // Create export data with exact field mapping
      const exportData = {
        virtual_assistant_name: va.hat_name || "",
        summary: va.summary || "",
        initial_prompt: va.initial_prompt || "",
        instructions: va.directions || ""
      };
      
      // Create JSON blob
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${va.hat_name || 'virtual_assistant'}_export.json`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Virtual Assistant exported successfully!");
    } catch (error) {
      console.error("Error exporting VA:", error);
      toast.error("Failed to export Virtual Assistant");
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return currentFilter !== 'all' || currentDepartmentFilter !== null || connectorFilter !== null || searchQuery.trim() !== '';
  };

  // Get filter display text
  const getFilterDisplayText = () => {
    switch (currentFilter) {
      case 'favorites':
        return 'Favorites';
      case 'my_hats':
        return 'My VAs';
      case 'public_hats':
        return 'Public VAs';
      case 'managed_hats':  
        return 'Managed VAs';  
      case 'shared_hats':  
        return 'Shared VAs';  
      case 'all':
        return 'All VAs';
      default:
        return 'All VAs'; 
    }
  };

  // Handle clicking the action menu
  // ISO timestamps out of DynamoDB; anything missing or unparseable reads
  // "Unknown" rather than "Invalid Date".
  const formatAuditDate = (value?: string) => {
    if (!value) return "Unknown";
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? "Unknown" : parsed.toLocaleString();
  };

  const toggleActionMenu = (vaId: string | undefined, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!vaId) return;
    setActionMenuOpen(actionMenuOpen === vaId ? null : vaId);
  };
  
  // Handle delete button click - shows modal instead of inline confirmation
  const handleDeleteClick = (va: Hat, e: React.MouseEvent) => {
    e.stopPropagation();
    if (va.id) {
      setVaToDelete(va);
      setShowDeleteModal(true);
      setActionMenuOpen(null); 
    } else {
      toast.error("Cannot delete: Missing VA ID");
    }
  };
  
  // Handle confirm delete
  const handleDelete = async () => {
    if (!vaToDelete?.id) {
      toast.error("Error: No Virtual Assistant selected for deletion");
      return;
    }
    
    setIsDeleting(true);
    try {
      await deleteVA(vaToDelete.id);
      setShowDeleteModal(false);
      setVaToDelete(null);
      // Let deleteVA in context handle the refresh
    } catch (err: any) {
      console.error("Error deleting VA:", err);
      if (err?.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error("Failed to delete Virtual Assistant");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAIModifyClick = (va: Hat, e: React.MouseEvent) => {
    e.stopPropagation();
    setVaToModify(va);
    setAiInstructions("");
    setShowAIHelp(false);
    setShowAIModifyModal(true);
    setActionMenuOpen(null);
  };

  const handleAIModifySubmit = async () => {
    if (!vaToModify?.id || !aiInstructions.trim()) return;
    setIsAIModifying(true);
    try {
      await modifyHatWithLLM(vaToModify.id, aiInstructions.trim());
      toast.success("Virtual Assistant modified successfully with AI!");
      setShowAIModifyModal(false);
      setVaToModify(null);
      setAiInstructions("");
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to modify Virtual Assistant with AI. Please try again.");
    } finally {
      setIsAIModifying(false);
    }
  };

  // Close all action menus when clicking outside
  useEffect(() => {
    const handleOutsideClick = () => {
      setActionMenuOpen(null);
    };
    
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  return (
    <>
      <div className="w-full p-3">
        {/* Header with search and create button */}
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-medium">Virtual Assistants</h2>
          {canCreateVA && (
            <button
              onClick={onCreateClick}
              className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1 rounded-md text-sm font-medium flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Create New VA
            </button>
          )}
        </div>
        
        {/* Search Input */}
        <div className="relative mb-3">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 bg-white text-gray-900 border border-gray-200 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            placeholder="Search Virtual Assistants"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Filter Button */}
        <button
          onClick={handleFilterClick}
          disabled={isFilterLoading}
          className={`w-full flex items-center justify-between px-3 py-2 mb-3 text-sm border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500 ${
            isFilterLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isFilterLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-violet-600 mr-2"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <span>{getFilterDisplayText()}</span>
              <Filter size={14} className="text-gray-400" />
            </>
          )}
        </button>

        {/* Department Filter Buttons */}
        <div className="mb-3">
          <DepartmentFilterButtons
            selectedDepartment={currentDepartmentFilter}
            onDepartmentChange={handleDepartmentChange}
            className="pb-2"
          />
        </div>

        {/* Clear All Filters Button (only show when filters are active) */}
        {hasActiveFilters() && (
          <div className="mb-3">
            <button
              onClick={handleClearAllFilters}
              className="w-full flex items-center justify-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md border border-gray-200"
            >
              <X size={14} className="mr-1" />
              Clear all filters
            </button>
          </div>
        )}
        
        {/* VA List as Cards */}
        <div className="space-y-3">
          {loading || isFilterLoading ? (
            <div className="bg-white rounded-lg shadow-sm p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                <span className="text-sm text-gray-500">
                  {isFilterLoading ? "Applying filter..." : "Loading Virtual Assistants..."}
                </span>
              </div>
            </div>
          ) : filteredVAs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-4 text-center">
              <p className="text-sm text-gray-500">
                {searchQuery || currentDepartmentFilter
                  ? "No Virtual Assistants match your search" 
                  : `No ${getFilterDisplayText().toLowerCase()} found`}
              </p>
            </div>
          ) : (
            filteredVAs.map((va) => (
              <div 
                key={va.id} 
                className="bg-white rounded-lg shadow-sm p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="font-medium text-primary truncate cursor-pointer hover:underline transition-colors duration-150"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVASelection(va);
                      }}
                      title={`Click to select: ${va.hat_name}`}
                    >
                      {va.hat_name}
                    </span>
                    {va.locked_by && (
                      <LockBadge
                        className="flex-shrink-0"
                        label={
                          va.locked_at
                            ? `Locked on ${new Date(va.locked_at).toLocaleDateString()}`
                            : "Locked"
                        }
                      />
                    )}
                 </div>
                  
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <button 
                      onClick={(e) => onFavoriteToggle && onFavoriteToggle(va, e)}
                      className="p-1 rounded-full focus:outline-none mr-1"
                      aria-label={isVAFavorite(va) ? "Remove from favorites" : "Add to favorites"}
                    >
                      <Star 
                        className={`h-5 w-5 ${isVAFavorite(va) ? "text-green-500 fill-current" : "text-gray-400"}`} 
                      />
                    </button>
                    
                    <div className="relative">
                      <button 
                        onClick={(e) => toggleActionMenu(va.id, e)}
                        className="p-1 rounded-full focus:outline-none text-gray-500"
                        aria-label="More options"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                        
                      {actionMenuOpen === va.id && (
                        <div 
                          className="absolute right-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 z-10 p-1"
                          onClick={(e) => e.stopPropagation()}
                          style={{ minWidth: '150px' }}
                        >
                          <div className="flex items-center justify-around space-x-1">
                            {/* Hidden when admin turned off VA creation */}
                            {canCreateVA && (
                            <button
                              className="p-2 rounded text-blue-600 hover:bg-gray-100"
                              onClick={() => onEditClick(va)}
                              aria-label="Edit"
                            >
                              <Edit className="h-5 w-5" />
                            </button>
                            )}
                            
                            {/* Duplicate -- hidden; onDuplicateClick stays wired
                                up so it can be brought back by un-commenting.
                            {onDuplicateClick && (
                              <button 
                                className="p-2 rounded text-violet-600 hover:bg-gray-100"
                                onClick={() => onDuplicateClick(va)}
                                aria-label="Duplicate"
                              >
                                <Copy className="h-5 w-5" />
                              </button>
                            )}
                            */}

                            <button
                              className="p-2 rounded text-gray-500 hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                setInfoOpenId(infoOpenId === va.id ? null : (va.id ?? null));
                                setActionMenuOpen(null);
                              }}
                              aria-label="Virtual Assistant details"
                            >
                              <Info className="h-5 w-5" />
                            </button>

                            <button
                              className="p-2 rounded text-green-600 hover:bg-gray-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportVA(va);
                                setActionMenuOpen(null);
                              }}
                              aria-label="Export/Download"
                            >
                              <Download className="h-5 w-5" />
                            </button>

                            {/* Modify with AI + Delete -- hidden when admin turned off VA creation */}
                            {canCreateVA && (
                            <>
                            <button
                              className={`p-2 rounded ${canEditVA(va) ? "text-amber-500 hover:bg-gray-100" : "text-gray-300 cursor-not-allowed"}`}
                              onClick={(e) => { e.stopPropagation(); if (canEditVA(va)) handleAIModifyClick(va, e); }}
                              aria-label={canEditVA(va) ? "Modify with AI" : "You don't have access to this VA"}
                              title={!canEditVA(va) ? "You don't have access to this VA" : "Modify with AI"}
                            >
                              <Wand2 className="h-5 w-5" />
                            </button>

                            <button
                              className="p-2 rounded text-red-600 hover:bg-gray-100"
                              onClick={(e) => handleDeleteClick(va, e)}
                              aria-label="Delete"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                            </>
                            )}

                            <LockButton
                              resourceType="va"
                              resourceId={va.id}
                              resource={va}
                              resourceName={va.hat_name}
                              // Same owner fields the isOwner checks above use:
                              // rows come back with user_id, older ones userId.
                              ownerId={(va as any).user_id || (va as any).userId}
                              onChange={fetchData}
                              className="p-2 rounded hover:bg-gray-100"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Show both type and department info */}
                <div className="flex flex-col text-xs text-gray-500 mt-1 space-y-1">
                  <div>
                    {!isAdmin ? getVAType(va, userId, currentFilter) : (va.is_public ? "Public" : "Private")}
                  </div>
                  <div>
                    Department: {(() => {
                      const department = va.department;
                      if (department && typeof department === 'string' && department.trim() !== '') {
                        return department;
                      } else {
                        return "No Department";
                      }
                    })()}
                  </div>
                </div>
                
                <div
                  className={`text-sm text-gray-700 mt-2 line-clamp-2 ${canCreateVA ? "cursor-pointer" : ""}`}
                  onClick={() => canCreateVA && onEditClick(va)}
                >
                  {va.summary}
                </div>

                {infoOpenId === va.id && (
                  <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500 space-y-1">
                    <div>
                      Created: <span className="text-gray-900">{formatAuditDate(va.created_at)}</span>
                    </div>
                    <div>
                      Created by: <span className="text-gray-900 break-all">{va.user_id || va.userId || "Unknown"}</span>
                    </div>
                    <div>
                      Last updated: <span className="text-gray-900">{formatAuditDate(va.updated_at)}</span>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !isDeleting && setShowDeleteModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {/* Warning Icon */}
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  
                  {/* Title */}
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 text-center mb-2">
                    Delete Virtual Assistant
                  </Dialog.Title>
                  
                  {/* Content */}
                  <div className="mb-6 text-center">
                    <p className="text-sm text-gray-500 mb-2">
                      Are you sure you want to delete
                    </p>
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      "{vaToDelete?.hat_name}"?
                    </p>
                    <p className="text-sm text-red-600 font-medium">
                      ⚠️ You will lose all data associated with this Virtual Assistant.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 justify-center">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:opacity-50"
                      onClick={() => {
                        setShowDeleteModal(false);
                        setVaToDelete(null);
                      }}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Deleting...
                        </div>
                      ) : (
                        'Delete'
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* AI Modify Modal */}
      <Transition appear show={showAIModifyModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !isAIModifying && setShowAIModifyModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-5 text-left align-middle shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-9 h-9 bg-amber-50 rounded-full">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">
                          Modify with AI
                        </Dialog.Title>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]">
                          {vaToModify?.hat_name}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => !isAIModifying && setShowAIModifyModal(false)}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      disabled={isAIModifying}
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-3">
                    Describe the changes you'd like AI to make to this Virtual Assistant.
                  </p>

                  {/* Instruction Input */}
                  <div className="mb-3">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <label htmlFor="mobile-ai-instructions" className="block text-sm font-medium text-gray-700">
                        Instructions
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowAIHelp(!showAIHelp)}
                        className="text-gray-400 hover:text-amber-500 transition-colors"
                        title="Click to see which fields AI can modify"
                      >
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {showAIHelp && (
                      <div className="mb-2 bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-xs text-gray-600">
                        <p className="font-medium text-gray-700 mb-1">AI can modify these fields:</p>
                        <ul className="space-y-0.5">
                          <li><span className="font-medium text-amber-600">Name</span> — Display name of the VA</li>
                          <li><span className="font-medium text-amber-600">Summary</span> — Brief description shown in the list</li>
                          <li><span className="font-medium text-amber-600">Initial Prompt</span> — First message sent to users</li>
                          <li><span className="font-medium text-amber-600">Instructions</span> — Behavior rules and guidelines</li>
                        </ul>
                      </div>
                    )}
                    <textarea
                      id="mobile-ai-instructions"
                      value={aiInstructions}
                      onChange={(e) => setAiInstructions(e.target.value)}
                      placeholder="e.g., Make the tone more professional, add customer complaint handling..."
                      className="w-full h-28 px-3 py-2 bg-white text-gray-900 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none placeholder:text-gray-400"
                      disabled={isAIModifying}
                      autoFocus
                    />
                  </div>

                  {/* AI Processing State */}
                  {isAIModifying && (
                    <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                      <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                      <span className="text-sm text-amber-700">AI is modifying your VA...</span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      className="flex-1 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      onClick={() => {
                        setShowAIModifyModal(false);
                        setVaToModify(null);
                        setAiInstructions("");
                      }}
                      disabled={isAIModifying}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="flex-1 inline-flex items-center justify-center rounded-md border border-transparent bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleAIModifySubmit}
                      disabled={isAIModifying || !aiInstructions.trim()}
                    >
                      {isAIModifying ? (
                        <div className="flex items-center">
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          Applying...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <Wand2 className="h-4 w-4 mr-2" />
                          Apply
                        </div>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Filter Modal */}
      <Transition appear show={showFilterModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowFilterModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Filter Virtual Assistants
                    </Dialog.Title>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="flex items-center text-sm font-medium text-gray-700 mb-3">
                        <Star size={16} className="mr-2 text-yellow-500" />
                        Filters
                      </h4>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="filter"
                            value="all"
                            checked={tempFilter === 'all'}
                            onChange={(e) => setTempFilter(e.target.value)}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">All VAs</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="filter"
                            value="favorites"
                            checked={tempFilter === 'favorites'}
                            onChange={(e) => setTempFilter(e.target.value)}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Favorites</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="filter"
                            value="my_hats"
                            checked={tempFilter === 'my_hats'}
                            onChange={(e) => setTempFilter(e.target.value)}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">My VAs</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="filter"
                            value="managed_hats"
                            checked={tempFilter === 'managed_hats'}
                            onChange={(e) => setTempFilter(e.target.value)}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Managed VAs</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="filter"
                            value="shared_hats"
                            checked={tempFilter === 'shared_hats'}
                            onChange={(e) => setTempFilter(e.target.value)}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Shared VAs</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="filter"
                            value="public_hats"
                            checked={tempFilter === 'public_hats'}
                            onChange={(e) => setTempFilter(e.target.value)}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">Public VAs</span>
                        </label>
                      </div>
                    </div>

                    {/* Connector filter -- narrows the list to VAs using a connector */}
                    {connectors.length > 0 && (
                      <div className="border-t pt-4">
                        <h4 className="flex items-center text-sm font-medium text-gray-700 mb-3">
                          <Settings2 size={16} className="mr-2 text-violet-500" />
                          Connectors
                        </h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="connectorFilter"
                              checked={tempConnectorFilter === null}
                              onChange={() => setTempConnectorFilter(null)}
                              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                            />
                            <span className="ml-2 text-sm text-gray-700">All Connectors</span>
                          </label>
                          {connectors.map((connector) => (
                            <label key={connector.id} className="flex items-center">
                              <input
                                type="radio"
                                name="connectorFilter"
                                checked={tempConnectorFilter === connector.id}
                                onChange={() => setTempConnectorFilter(connector.id)}
                                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                              />
                              <span className="ml-2 text-sm text-gray-700 truncate">
                                {connector.name}
                              </span>
                              <span className="ml-2 text-xs text-gray-400 capitalize">
                                {connector.type}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex justify-between">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                      onClick={handleClearFilters}
                    >
                      Clear all filters
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                      onClick={handleApplyFilter}
                    >
                      Done
                   </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default MobileVAListView;